import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  message,
  Divider,
  Input,
  List,
  Typography,
  Card,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getSocialMessage,
  getSocialMessages,
  markMessageRead,
  replyToMessage,
  type SocialMessage,
} from '@/api/socialApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;
const { Text } = Typography;

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'blue',
  instagram: 'magenta',
  twitter: 'cyan',
  linkedin: 'geekblue',
  wechat: 'green',
};

export default function SocialMessageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [msg, setMsg] = useState<SocialMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversationMessages, setConversationMessages] = useState<SocialMessage[]>([]);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canWrite = hasPermission('social_messages', 'write');

  const loadMessage = useCallback((msgId: string) => {
    setLoading(true);
    setError(null);
    getSocialMessage(msgId)
      .then((data) => {
        setMsg(data);
        if (data.conversation_id) {
          getSocialMessages({ from: 0, size: 50, conversation_id: data.conversation_id })
            .then((res) => {
              const sorted = [...res.items].sort(
                (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
              );
              setConversationMessages(sorted);
            })
            .catch(() => setConversationMessages([]));
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    if (id) loadMessage(id);
  }, [id, loadMessage]);

  const handleMarkRead = useCallback(async () => {
    if (!id) return;
    try {
      await markMessageRead(id);
      message.success(t('social.action.mark_read_success'));
      loadMessage(id);
    } catch {
      message.error(t('app.error'));
    }
  }, [id, t, loadMessage]);

  const handleReply = useCallback(async () => {
    if (!id || !replyContent.trim()) return;
    setSubmitting(true);
    try {
      await replyToMessage(id, { content: replyContent.trim() });
      message.success(t('social.action.reply_success'));
      setReplyContent('');
      loadMessage(id);
    } catch {
      message.error(t('app.error'));
    } finally {
      setSubmitting(false);
    }
  }, [id, replyContent, t, loadMessage]);

  if (loading) return <Spin size="large" />;
  if (error) return <Alert type="error" message={error} />;
  if (!msg) return <Alert type="warning" message={t('app.not_found')} />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/social/messages')}>
          {t('app.back')}
        </Button>
        {canWrite && msg.status === 'unread' && (
          <Button icon={<CheckOutlined />} onClick={handleMarkRead}>
            {t('social.action.mark_read')}
          </Button>
        )}
      </Space>

      <h2>{t('social.message_detail_title')}</h2>

      <Descriptions bordered column={2}>
        <Descriptions.Item label={t('social.field.content')} span={2}>
          {msg.content}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.platform')}>
          <Tag color={PLATFORM_COLORS[msg.platform] ?? 'default'}>
            {t(`social.platform.${msg.platform}`, msg.platform)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.direction')}>
          <Tag color={msg.direction === 'inbound' ? 'blue' : 'green'}>
            {t(`social.direction.${msg.direction}`, msg.direction)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.status')}>
          <Tag color={msg.status === 'unread' ? 'orange' : 'default'}>
            {t(`social.status.${msg.status}`, msg.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.message_type')}>
          {msg.message_type}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.account')}>
          <a onClick={() => navigate(`/social/accounts/${msg.account_id}`)}>
            {msg.account_id}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.contact_id')}>
          {msg.contact_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.conversation_id')}>
          {msg.conversation_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.replied_at')}>
          {msg.replied_at ? new Date(msg.replied_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.branch_id')}>
          {msg.branch_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.external_message_id')}>
          {msg.external_message_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.created_by')}>
          {msg.created_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.created_at')}>
          {new Date(msg.created_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.updated_by')}>
          {msg.updated_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.updated_at')}>
          {new Date(msg.updated_at).toLocaleString()}
        </Descriptions.Item>
      </Descriptions>

      {/* Conversation history */}
      {conversationMessages.length > 0 && (
        <>
          <Divider />
          <h3>{t('social.section.conversation')}</h3>
          <List
            dataSource={conversationMessages}
            renderItem={(item) => (
              <List.Item
                style={{
                  background: item.id === id ? '#f0f5ff' : undefined,
                  padding: '8px 16px',
                }}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Tag color={item.direction === 'inbound' ? 'blue' : 'green'}>
                        {t(`social.direction.${item.direction}`, item.direction)}
                      </Tag>
                      <Text type="secondary">{new Date(item.created_at).toLocaleString()}</Text>
                    </Space>
                  }
                  description={item.content}
                />
              </List.Item>
            )}
          />
        </>
      )}

      {/* Reply form */}
      {canWrite && msg.direction === 'inbound' && (
        <>
          <Divider />
          <h3>{t('social.action.reply')}</h3>
          <Card size="small">
            <TextArea
              rows={3}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder={t('social.action.reply')}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              style={{ marginTop: 8 }}
              loading={submitting}
              disabled={!replyContent.trim()}
              onClick={handleReply}
            >
              {t('social.action.reply')}
            </Button>
          </Card>
        </>
      )}
    </div>
  );
}
